import { useEffect, useRef, useState } from 'react'

import { EMPTY_LIST } from '../../../../constants/common'
import { getItemsFromApiResponse } from '../../../../utils/api'
import { preventDefault } from '../../../../utils/events'
import { InputAsyncSelect, MultiSelect } from '../../../Select'
import { EMAIL_CREATION_FORM_RECIPIENT_FIELD_EXPAND_API_PARAM } from '../../EmailCreationForm'
import { getRecipients } from '../EmailRecipientsField.api'
import { toEmailRecipientFieldSelectItemsFromOption } from '../EmailRecipientsField.utils'
import { RecipientTag } from '../RecipientTag'
import { SelectButton } from '../SelectButton'

import {
  API_PARAMS,
  MIN_SEARCH_LENGTH,
} from './EmailRecipientsSelect.constants'
import {
  useEmailRecipientsSelect,
  useEmailRecipientsSelectActions,
} from './EmailRecipientsSelect.hooks'
import {
  getExcludeApiParam,
  getRecipientsSearchParam,
} from './EmailRecipientsSelect.utils'
import { EmailRecipientsSelectDropdownHeader } from './EmailRecipientsSelectDropdownHeader'
import { EmailRecipientsSelectDropdownItemIcons } from './EmailRecipientsSelectDropdownItemIcons'

import * as styles from './EmailRecipientsSelect.module.css'

function EmailRecipientsSelect({
  FilterButton,
  Tag = RecipientTag,
  autoAddEnabled,
  blocked,
  droppableIndex,
  errorItems = EMPTY_LIST,
  errorOptionKeys,
  errorText,
  expandApiParam = EMAIL_CREATION_FORM_RECIPIENT_FIELD_EXPAND_API_PARAM,
  filterModalInitialValues,
  initShowMoreValue,
  inputProps,
  isDragDisabled,
  isDropDisabled,
  name,
  placeholder,
  printer,
  showError,
  value,
  viewBcc,
  viewCc,
  onChange,
  onChangeAutoAddEnabled,
  onClickBcc,
  onClickCc,
  onCloseModal,
  onExpandSelectedRecipientEmails,
  onOpenFilterModal,
  onSetMassMailing,
  onSetModal,
  onValidate,
  ...restProps
}) {
  const [dropdownHidden, setDropdownHidden] = useState(false)

  const inputElRef = useRef(null)
  const selectStatesRef = useRef(null)

  // Toggling auto-add changes `apiParams`, but the async-select hook only
  // refetches on dropdown open or search change. If the dropdown is already
  // open with a search term, trigger a reload manually so the results reflect
  // the new params immediately.
  useEffect(() => {
    if (
      selectStatesRef.current?.isOpen &&
      selectStatesRef.current?.searchValue?.length >= MIN_SEARCH_LENGTH
    ) {
      selectStatesRef.current.handleChangeSearch(
        selectStatesRef.current.searchValue
      )
    }
  }, [autoAddEnabled])

  const apiParams = {
    ...API_PARAMS,
    ...(autoAddEnabled ? expandApiParam : null),
    ...getExcludeApiParam(value),
  }

  function handleInputVisibilityChange(visible) {
    setDropdownHidden(!visible)
  }

  function clearSearchInSelect() {
    selectStatesRef.current?.handleClearSearch?.()
  }

  function closeSelect() {
    selectStatesRef.current?.handleClose?.()
  }

  const {
    handleBlur,
    handleChangeSearch,
    handleClearAll,
    handleCloseDropdown,
    handleDeleteOption,
    handleKeyDown,
    handlePaste,
    handleSelectItem,
    updateValue,
  } = useEmailRecipientsSelect({
    clearSearchInSelect,
    closeSelect,
    inputElRef,
    selectStatesRef,
    value,
    onChange,
    onExpandSelectedRecipientEmails,
    onValidate,
  })

  const { handleClickBcc, handleClickCc, handleOpenFilterModal } =
    useEmailRecipientsSelectActions({
      autoAddEnabled,
      closeSelect,
      filterModalInitialValues,
      updateValue,
      onClickBcc,
      onClickCc,
      onCloseModal,
      onOpenFilterModal,
      onSetMassMailing,
      onSetModal,
    })

  const mergedInputProps = {
    ...inputProps,
    inputRef: node => {
      inputElRef.current = node
    },
    onBlur: handleBlur,
    onInputVisibilityChange: handleInputVisibilityChange,
    onKeyDown: handleKeyDown,
    onPaste: handlePaste,
  }

  const buttonProps = {
    actionsProps: {
      FilterButton,
      blocked,
      viewBcc,
      viewCc,
      onClickBcc: handleClickBcc,
      onClickCc: handleClickCc,
      onOpenFilterModal: handleOpenFilterModal,
    },
    blocked,
    droppableIndex,
    errorText,
    initShowMoreValue,
    inputProps: mergedInputProps,
    isDropDisabled,
    name,
    placeholder,
    selectedOptionsProps: {
      Tag,
      errorItems,
      errorOptionKeys,
      isDragDisabled,
      printer,
      values: value,
    },
    showError,
    value,
    onClearAll: handleClearAll,
    onDeleteOption: handleDeleteOption,
  }

  const headerProps = onChangeAutoAddEnabled
    ? {
        autoAddEnabled,
        onChangeAutoAdd: onChangeAutoAddEnabled,
      }
    : null

  return (
    <MultiSelect
      {...restProps}
      Control={SelectButton}
      Select={InputAsyncSelect}
      Tags={null}
      apiParams={apiParams}
      blocked={blocked}
      controlProps={buttonProps}
      dropdownProps={{
        ...(dropdownHidden ? { className: styles.hiddenDropdown } : null),
      }}
      getData={getRecipients}
      getDataItems={getItemsFromApiResponse}
      getSearchApiParam={getRecipientsSearchParam}
      listProps={{
        Header: onChangeAutoAddEnabled
          ? EmailRecipientsSelectDropdownHeader
          : null,
        headerProps,
        itemProps: {
          contentProps: { Icons: EmailRecipientsSelectDropdownItemIcons },
          onMouseDown: preventDefault,
        },
      }}
      minSearchLength={MIN_SEARCH_LENGTH}
      selectStatesRef={selectStatesRef}
      toPreparedItems={toEmailRecipientFieldSelectItemsFromOption}
      value={value}
      onChangeSearch={handleChangeSearch}
      onCloseDropdown={handleCloseDropdown}
      onSelectItem={handleSelectItem}
    />
  )
}

export default EmailRecipientsSelect
